/**
 * The API, as the app talks to it.
 *
 * One `Api` interface, one implementation over `fetch`, and screens that take the
 * interface rather than reaching for the network: that is what lets the whole
 * loop be driven in a test with a fake, and driven in a browser by the real one,
 * without a screen knowing which it has.
 *
 * Every failure is an `ApiError` carrying the status and the API's own error
 * code, because the routes answer `{ error, detail }` on purpose: a screen shows
 * `detail` to the user and can branch on `code`. A network failure that never
 * reached the API is `status: 0, code: 'network'`, which is the one case where
 * retrying is obviously right.
 *
 * `baseUrl` is the one thing a screen never sets: a browser tab talks to its own
 * origin, and the installed app talks to the server named in the APK (see
 * `origin.ts`). It changes the URL and the credentials mode together, which is why
 * it is passed in rather than concatenated at each call.
 */

import { apiUrl, credentialsFor, SAME_ORIGIN } from './origin.js';
import type {
  Bin,
  CollectionCopy,
  Container,
  Copy,
  CopyLocation,
  IntakeSession,
  Meta,
  Picklist,
  PicklistItem,
  PrintingRecord,
  RecognitionResponse,
  ScanRequest,
  ScanResult,
  Shortfall,
  User,
} from './types.js';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface Api {
  meta(): Promise<Meta>;

  register(input: { email: string; password: string; displayName?: string }): Promise<User>;
  signIn(input: { email: string; password: string }): Promise<User>;
  signOut(): Promise<void>;
  me(): Promise<User>;

  containers(): Promise<Container[]>;
  createContainer(input: { label: string; kind: string; sortOrder?: number }): Promise<Container>;
  /** Rename a container, change its kind, or both; answers the container as it now is. */
  updateContainer(publicId: string, patch: { label?: string; kind?: string }): Promise<Container>;
  createBin(containerPublicId: string, input: { label: string; sortOrder?: number }): Promise<void>;
  /** Rename a bin; answers the bin as it now is. */
  updateBin(containerPublicId: string, binPublicId: string, patch: { label: string }): Promise<Bin>;
  /** Remove an empty container with its empty bins; 409 when cards remain. */
  deleteContainer(containerPublicId: string): Promise<void>;
  /** Remove an empty bin; 409 `bin_not_empty` when it still holds cards. */
  deleteBin(containerPublicId: string, binPublicId: string): Promise<void>;
  /** Every shelved card with its printing name and shelf address. */
  collection(): Promise<{ copies: CollectionCopy[]; count: number }>;

  openIntakeSession(input: {
    label?: string;
    targetBinPublicId: string;
    recognitionMode?: string;
  }): Promise<IntakeSession>;
  commitScans(sessionPublicId: string, scans: ScanRequest[]): Promise<{ intakeSession: IntakeSession; results: ScanResult[] }>;
  closeIntakeSession(sessionPublicId: string): Promise<IntakeSession>;
  /** Read back a session the client remembered across a reload. */
  intakeSession(sessionPublicId: string): Promise<IntakeSession>;

  /** Resolve a capture's fingerprint into a printing, or into candidates to review. */
  /** The art window cut once, or cut several ways: the server takes the best framing. */
  recognise(input: {
    artPhash: string | readonly string[];
    frameClass?: string;
    mode?: string;
    scanId?: string;
  }): Promise<RecognitionResponse>;

  /** The printing a card is, from what is printed on it. */
  lookupPrintings(input: { setCode: string; number: string }): Promise<PrintingRecord[]>;

  createPicklist(input: {
    label: string;
    strategy?: string;
    requests: Array<{ printingId: number; quantity: number }>;
  }): Promise<{ picklist: Picklist; items: PicklistItem[]; shortfalls: Shortfall[] }>;
  picklist(publicId: string): Promise<{ picklist: Picklist; items: PicklistItem[] }>;
  markPicked(publicId: string, position: number): Promise<{ picklist: Picklist; items: PicklistItem[] }>;

  copy(publicId: string): Promise<{ copy: Copy; location: CopyLocation | null }>;
  /** Change a copy's finish, condition and notes; absent fields are left alone. */
  updateCopy(
    publicId: string,
    patch: { finish?: string; condition?: string; notes?: string | null },
  ): Promise<{ copy: Copy; location: CopyLocation | null }>;
  /** Remove a copy for good; the shelf slot stays retired. */
  deleteCopy(publicId: string): Promise<void>;

  /** Collection export as ManaBox-compatible CSV, for one scope. */
  exportCsv(input: { scope: 'collection' | 'container' | 'bin'; id?: string }): Promise<{
    filename: string;
    csv: string;
    count: number;
  }>;
}

export interface HttpApiOptions {
  /** Injected in tests; defaults to the window's `fetch`. */
  fetchImpl?: typeof fetch;
  /** The server to call when it is not this origin; `''` (the default) means here. */
  baseUrl?: string;
}

export interface RequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
  fetchImpl?: typeof fetch;
  /** The server to call when it is not this origin; `''` (the default) means here. */
  baseUrl?: string;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const doFetch = options.fetchImpl ?? fetch;
  const method = options.method ?? 'GET';
  const baseUrl = options.baseUrl ?? SAME_ORIGIN;
  let response: Response;
  try {
    response = await doFetch(apiUrl(path, baseUrl), {
      method,
      signal: options.signal,
      // The session is a cookie, so it travels with the request rather than in a
      // header this client would have to remember to add - and `include` when the
      // API is another origin, where `same-origin` would drop it.
      credentials: credentialsFor(baseUrl),
      headers: {
        accept: 'application/json',
        ...(options.body === undefined ? {} : { 'content-type': 'application/json' }),
      },
      ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    });
  } catch (error) {
    throw new ApiError(0, 'network', `could not reach the API: ${error instanceof Error ? error.message : 'unknown error'}`);
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  let payload: unknown = null;
  if (text !== '') {
    try {
      payload = JSON.parse(text);
    } catch {
      throw new ApiError(response.status, 'invalid_response', `the API answered ${response.status} with something that is not JSON`);
    }
  }

  if (!response.ok) {
    const body = (payload ?? {}) as { error?: string; detail?: string };
    throw new ApiError(response.status, body.error ?? 'unknown', body.detail ?? `the API answered ${response.status}`);
  }

  return payload as T;
}

export function createHttpApi(options: HttpApiOptions = {}): Api {
  const call = <T>(path: string, init: { method?: string; body?: unknown } = {}): Promise<T> =>
    request<T>(path, { ...init, fetchImpl: options.fetchImpl, baseUrl: options.baseUrl ?? SAME_ORIGIN });

  return {
    meta: () => call<Meta>('/api/meta'),

    register: async (input) => (await call<{ user: User }>('/api/accounts', { method: 'POST', body: input })).user,
    signIn: async (input) => (await call<{ user: User }>('/api/sessions', { method: 'POST', body: input })).user,
    signOut: () => call<void>('/api/sessions', { method: 'DELETE' }),
    me: async () => (await call<{ user: User }>('/api/me')).user,

    containers: async () => (await call<{ containers: Container[] }>('/api/containers')).containers,
    createContainer: async (input) =>
      (await call<{ container: Container }>('/api/containers', { method: 'POST', body: input })).container,
    updateContainer: async (publicId, patch) =>
      (
        await call<{ container: Container }>(`/api/containers/${encodeURIComponent(publicId)}`, {
          method: 'PATCH',
          body: patch,
        })
      ).container,
    updateBin: async (containerPublicId, binPublicId, patch) =>
      (
        await call<{ bin: Bin }>(
          `/api/containers/${encodeURIComponent(containerPublicId)}/bins/${encodeURIComponent(binPublicId)}`,
          { method: 'PATCH', body: patch },
        )
      ).bin,
    createBin: async (containerPublicId, input) => {
      await call(`/api/containers/${encodeURIComponent(containerPublicId)}/bins`, { method: 'POST', body: input });
    },
    deleteContainer: async (containerPublicId) => {
      await call(`/api/containers/${encodeURIComponent(containerPublicId)}`, { method: 'DELETE' });
    },
    deleteBin: async (containerPublicId, binPublicId) => {
      await call(`/api/containers/${encodeURIComponent(containerPublicId)}/bins/${encodeURIComponent(binPublicId)}`, {
        method: 'DELETE',
      });
    },
    collection: () => call<{ copies: CollectionCopy[]; count: number }>('/api/collection'),

    openIntakeSession: async (input) =>
      (await call<{ intakeSession: IntakeSession }>('/api/intake-sessions', { method: 'POST', body: input })).intakeSession,
    commitScans: (sessionPublicId, scans) =>
      call<{ intakeSession: IntakeSession; results: ScanResult[] }>(
        `/api/intake-sessions/${encodeURIComponent(sessionPublicId)}/scans`,
        { method: 'POST', body: { scans } },
      ),
    closeIntakeSession: async (sessionPublicId) =>
      (
        await call<{ intakeSession: IntakeSession }>(
          `/api/intake-sessions/${encodeURIComponent(sessionPublicId)}/close`,
          { method: 'POST', body: {} },
        )
      ).intakeSession,

    intakeSession: async (sessionPublicId) =>
      (
        await call<{ intakeSession: IntakeSession }>(
          `/api/intake-sessions/${encodeURIComponent(sessionPublicId)}`,
        )
      ).intakeSession,

    recognise: (input) => call<RecognitionResponse>('/api/scans/recognise', { method: 'POST', body: input }),

    lookupPrintings: async (input) => {
      const query = new URLSearchParams({ set: input.setCode, number: input.number });
      return (await call<{ printings: PrintingRecord[] }>(`/api/catalog/printings?${query.toString()}`)).printings;
    },

    createPicklist: (input) =>
      call<{ picklist: Picklist; items: PicklistItem[]; shortfalls: Shortfall[] }>('/api/picklists', {
        method: 'POST',
        body: input,
      }),
    picklist: (publicId) =>
      call<{ picklist: Picklist; items: PicklistItem[] }>(`/api/picklists/${encodeURIComponent(publicId)}`),
    markPicked: (publicId, position) =>
      call<{ picklist: Picklist; items: PicklistItem[] }>(
        `/api/picklists/${encodeURIComponent(publicId)}/items/${position}/picked`,
        { method: 'POST', body: {} },
      ),

    copy: (publicId) => call<{ copy: Copy; location: CopyLocation | null }>(`/api/copies/${encodeURIComponent(publicId)}`),
    updateCopy: (publicId, patch) =>
      call<{ copy: Copy; location: CopyLocation | null }>(`/api/copies/${encodeURIComponent(publicId)}`, {
        method: 'PATCH',
        body: patch,
      }),
    deleteCopy: async (publicId) => {
      await call(`/api/copies/${encodeURIComponent(publicId)}`, { method: 'DELETE' });
    },

    exportCsv: (input) => {
      const query = new URLSearchParams({ scope: input.scope, ...(input.id ? { id: input.id } : {}) });
      return call<{ filename: string; csv: string; count: number }>(`/api/exports?${query.toString()}`);
    },
  };
}
